/**
 * Raw HTML in a chat reply (enabled so citation placeholders render) is
 * filtered to what markdown itself produces. A reply can quote a web page or
 * a document, so anything it carries in is untrusted: iframes, forms,
 * scripts, styles and inline style attributes (overlays) are dropped, and
 * only the attributes markdown and the citation placeholders use survive.
 */ /** Elements kept as they are. */ const ALLOWED = new Set([
    'p',
    'br',
    'hr',
    'h1',
    'h2',
    'h3',
    'h4',
    'h5',
    'h6',
    'ul',
    'ol',
    'li',
    'a',
    'strong',
    'b',
    'em',
    'i',
    'del',
    's',
    'u',
    'code',
    'pre',
    'blockquote',
    'table',
    'thead',
    'tbody',
    'tfoot',
    'tr',
    'th',
    'td',
    'span',
    'div',
    'sup',
    'sub',
    'mark',
    'kbd',
    'small',
    'details',
    'summary',
    'input',
    'img',
    'section'
]);
/** Elements dropped with everything inside them. */ const DROPPED = new Set([
    'script',
    'style',
    'iframe',
    'frame',
    'frameset',
    'object',
    'embed',
    'form',
    'button',
    'select',
    'textarea',
    'link',
    'meta',
    'base',
    'svg',
    'math',
    'template',
    'noscript',
    'video',
    'audio',
    'canvas'
]);
/** Attributes kept, per element ('*' = any element). */ const ATTRS = {
    '*': new Set([
        'className',
        'id',
        'title'
    ]),
    a: new Set([
        'href'
    ]),
    span: new Set([
        'dataCitationId',
        'dataStructuredCitationId'
    ]),
    th: new Set([
        'align'
    ]),
    td: new Set([
        'align'
    ]),
    input: new Set([
        'type',
        'checked',
        'disabled'
    ]),
    img: new Set([
        'src',
        'alt',
        'width',
        'height'
    ]),
    ol: new Set([
        'start'
    ])
};
/** Images only from this app (no tracking pixels from a quoted page). */ function sameOrigin(src) {
    if (typeof src !== 'string') return false;
    if (src.startsWith('/') && !src.startsWith('//')) return true;
    try {
        return typeof window !== 'undefined' && new URL(src).origin === window.location.origin;
    } catch  {
        return false;
    }
}
function clean(node) {
    if (node.type !== 'element') return [
        node
    ];
    const tag = node.tagName ?? '';
    if (DROPPED.has(tag)) return [];
    const children = (node.children ?? []).flatMap(clean);
    // An unknown element keeps its text but not its tag.
    if (!ALLOWED.has(tag)) return children;
    if (tag === 'input' && node.properties?.type !== 'checkbox') return [];
    if (tag === 'img' && !sameOrigin(node.properties?.src)) return [];
    const props = {};
    for (const [k, v] of Object.entries(node.properties ?? {})){
        if (ATTRS['*'].has(k) || ATTRS[tag]?.has(k)) props[k] = v;
    }
    return [
        {
            ...node,
            properties: props,
            children
        }
    ];
}
/** A rehype plugin: run after rehype-raw. */ export function rehypeAllowlist() {
    return (tree)=>{
        tree.children = (tree.children ?? []).flatMap(clean);
    };
}
